/**
 * Servizi puri per il level-up del PG (D1 della Fase D — build planner).
 *
 * **Scope Fase D**: il LevelUpFlow programma la build del PG (decide stat,
 * mossa, archetipo IC, eventuali spell-add) ma NON consuma PE. Il consumo
 * di `level + 7` PE è rinviato a Fase F (scheda giocabile), dove il bottone
 * «Avanza al livello programmato» applicherà la programmazione + sottrarrà
 * i PE.
 *
 * Riferimento: `dev-workspace/docs/Fase-D-Level-Up-SSOT.md`.
 */

import type { ClassDefinition } from '../schemas/classDefinition';
import { CharacterSchema, type Character, type MoveRef, type SpellRef } from '../schemas/character';
import type { ClassId } from '../schemas/classId';
import type { LevelUpRecord } from '../schemas/levelUpRecord';
import type { Move, Prerequisite } from '../schemas/move';
import type { StatId } from '../schemas/stats';
import {
  findMoveById,
  findMoveByIdAnyScope,
  findMoveByIdGlobal,
  movesByClass,
} from '../data/moves';
import type { AcquiredMove } from '../schemas/moveAcquisition';
import { computeMaxHp } from './characterCreation';
import { getFollowersFromDraft } from './followerCreation';
import { buildLevelUpRecord } from './levelUp.audit';
import { getGrantedPoolSize } from './moveGrants';
import { applyMoveAcquisition, type ApplyMoveAcquisitionOptions } from './moveAcquisition';

// ---------------------------------------------------------------------------
// Costanti
// ---------------------------------------------------------------------------

/**
 * Cap del livello per le formule level-derived (manuale V1 r. 1834,
 * D10 / FI-11). Le mosse il cui effetto dipende dal livello (es. *Mana*)
 * usano `getCappedLevel(character)` invece di `character.level` direttamente;
 * il livello assoluto del PG (`Character.level`) può eccedere 10 quando ha
 * scelto Instancabile/Apprendista.
 */
export const LEVEL_FORMULA_CAP = 10;

/** Cap massimo di un punteggio di Caratteristica (manuale V1 r. 1760). */
export const STAT_CAP = 18;

// ---------------------------------------------------------------------------
// Predicati: è possibile fare level-up?
// ---------------------------------------------------------------------------

/**
 * Il PG può programmare il livello successivo?
 *
 * Regole (post-D10 / FI-11):
 *  - L1..L10 → sempre disponibile (passaggio standard verso il livello +1)
 *  - L10 → disponibile verso L11 (passaggio cruciale, il giocatore sceglie
 *    `pastTen` nel flow di level-up)
 *  - L>10 + `pastTen === 'addio'` → NO (PG ritirato)
 *  - L>10 + `pastTen ∈ {apprendista, instancabile}` → SI (continua a crescere)
 *  - L>10 + `pastTen` undefined → NO (stato anomalo, scelta non fatta)
 *
 * Niente gate PE (rinviato a Fase F).
 */
export function isLevelUpAvailable(character: Character): boolean {
  if (character.level <= LEVEL_FORMULA_CAP) return true;
  return character.pastTen === 'apprendista' || character.pastTen === 'instancabile';
}

/**
 * **D10 — FI-11**: livello cappato a 10 per le formule level-derived (Mana
 * count, hp scaling gregari, durata effetti). Per PG L1-L10 ritorna
 * `character.level`; per PG L11+ ritorna `10` indipendentemente dal
 * livello assoluto (manuale V1 r. 1834: l'Instancabile considera *sempre*
 * un livello *massimo* di *10*).
 *
 * Helper esposto per i consumer rilevanti (vedi `MoveSchema.grantsSpells`
 * con `levelRule: 'lte-character-level'`, ecc.). I consumer che leggono
 * `Character.level` per **identità** (es. "quanti level-up ha fatto") NON
 * usano questo helper.
 */
export function getCappedLevel(character: Character): number {
  return Math.min(character.level, LEVEL_FORMULA_CAP);
}

/**
 * **D10 — FI-11**: il PG ha effettuato la scelta post-L10 e continua a
 * crescere (Apprendista senior o Instancabile)? Se sì, le 5 mosse comuni
 * multiclasse a catena (Apprendista → ... → Gran Maestro) sono accessibili,
 * e le Caratteristiche non aumentano più (`applyLevelUp` salta `statIncreased`).
 */
export function hasAccessToMulticlassCommonMoves(character: Character): boolean {
  return character.pastTen === 'apprendista' || character.pastTen === 'instancabile';
}

/**
 * PE necessari per il prossimo level-up secondo la regola del manuale
 * (V1 r. 1750): `level + 7`. Helper di display — in Fase D non blocca,
 * mostriamo solo l'info `xp / xpRequired` nella UI.
 */
export function getXpRequired(character: Character): number {
  return character.level + 7;
}

/**
 * Quale livello raggiungerà il PG al prossimo level-up?
 * Convenienza per il LevelUpFlow.
 */
export function getNextLevel(character: Character): number {
  return character.level + 1;
}

// ---------------------------------------------------------------------------
// Stat: quali sono aumentabili?
// ---------------------------------------------------------------------------

/**
 * Per ogni Caratteristica, indica se è al cap (`STAT_CAP = 18`) e quindi
 * NON aumentabile al level-up corrente. Le altre tornano `false`.
 *
 * Usato dalla UI dello step «+1 Stat» per disabilitare le card delle stat
 * già al massimo.
 */
export function getStatCapForLevelUp(character: Character): Record<StatId, boolean> {
  return {
    forza: character.stats.forza >= STAT_CAP,
    destrezza: character.stats.destrezza >= STAT_CAP,
    costituzione: character.stats.costituzione >= STAT_CAP,
    intelligenza: character.stats.intelligenza >= STAT_CAP,
    saggezza: character.stats.saggezza >= STAT_CAP,
    carisma: character.stats.carisma >= STAT_CAP,
  };
}

// ---------------------------------------------------------------------------
// Mosse: catalogo disponibile al level-up
// ---------------------------------------------------------------------------

/**
 * Verifica se TUTTI i `prerequisites` di una mossa sono soddisfatti dalle
 * mosse già possedute dal PG.
 *
 * Semantica:
 *  - lista vuota → sempre soddisfatto
 *  - stringa singola → quella mossa deve essere in `ownedMoveIds`
 *  - `allOf` → tutte le mosse elencate devono essere in `ownedMoveIds`
 *  - `anyOf` → almeno una mossa elencata deve essere in `ownedMoveIds`
 *  - più entry nell'array `prerequisites[]` → AND tra le entry
 */
export function arePrerequisitesSatisfied(
  prerequisites: readonly Prerequisite[],
  ownedMoveIds: ReadonlySet<string>,
): boolean {
  return prerequisites.every((p) => isPrereqSatisfied(p, ownedMoveIds));
}

function isPrereqSatisfied(p: Prerequisite, owned: ReadonlySet<string>): boolean {
  if (typeof p === 'string') return owned.has(p);
  if ('allOf' in p) return p.allOf.every((id) => owned.has(id));
  return p.anyOf.some((id) => owned.has(id));
}

/**
 * Mosse advanced della classe del PG **disponibili** al level-up verso
 * `targetLevel`. Filtri applicati:
 *  1. **Tipo compatibile col livello**: `advanced_2_5` per `targetLevel <= 5`,
 *     `advanced_6_10` per `targetLevel >= 6`. (Una mossa `advanced_2_5` non
 *     diventa indisponibile a L6+: il PG può comunque prenderla; vedi nota
 *     sotto sulla decisione di scope D1.)
 *  2. **Prerequisiti soddisfatti** dalle mosse correnti
 *  3. **Mossa non già posseduta** (no duplicati)
 *  4. **Se ha `replaces`**: la mossa-target da soppiantare deve essere posseduta
 *
 * **Decisione di scope D1**: in D1 trattiamo solo `targetLevel === 2`. Il
 * filtro tipo lascia passare le `advanced_2_5` (corrette per L2-5) e
 * `advanced_6_10` non sono ancora pertinenti (FI-11 / D4). Il predicato è
 * già preparato per livelli successivi (D3-D4).
 *
 * **Fuori scope D1**: mosse multiclasse (FI-17 → D7), grants cross-class
 * (FI-30/31 → D5/D6).
 */
export function getAvailableAdvancedMoves(
  character: Character,
  targetLevel: number,
  classDef: ClassDefinition,
): readonly Move[] {
  const ownedIds = new Set(character.moves.map((m) => m.id));
  const allClassMoves = getClassMoveCatalog(classDef.id);
  return allClassMoves.filter((move) => {
    if (!isAdvancedMoveAvailable(move, targetLevel, ownedIds)) return false;
    // FI-31 (D5): una mossa con `grantsMoveChoice` è selezionabile solo se
    // il pool residuo della sotto-scelta è ≥ 1. Es: PG con tutti gli
    // Istinti già presi → *Istinti Primordiali* filtrata fuori.
    if (
      move.grantsMoveChoice !== undefined &&
      getGrantedPoolSize(character, move.grantsMoveChoice, classDef) === 0
    ) {
      return false;
    }
    return true;
  });
}

function isAdvancedMoveAvailable(
  move: Move,
  targetLevel: number,
  ownedIds: ReadonlySet<string>,
): boolean {
  // Tipo compatibile col target level
  if (move.type === 'advanced_2_5' && targetLevel > 5) return false;
  if (move.type === 'advanced_6_10' && targetLevel < 6) return false;
  if (move.type !== 'advanced_2_5' && move.type !== 'advanced_6_10') return false;
  // Non già posseduta
  if (ownedIds.has(move.id)) return false;
  // Prereq soddisfatti
  if (!arePrerequisitesSatisfied(move.prerequisites, ownedIds)) return false;
  // Se `replaces` non vuoto: il PG deve avere la mossa da soppiantare
  if (move.replaces.length > 0 && !move.replaces.some((id) => ownedIds.has(id))) return false;
  return true;
}

function getClassMoveCatalog(classId: ClassDefinition['id']): readonly Move[] {
  return movesByClass[classId] ?? [];
}

// `getAvailableCommonMoves` (D1 + D10) estratto in `./commonMovesAvailability.ts`
// per rispettare `max-lines: 400` su questo file.
export { getAvailableCommonMoves } from './commonMovesAvailability';

// `validateLevelUpInput` + sub-validators estratti in `./levelUp.validation.ts`
// per rispettare `max-lines: 400` (D10).
import { validateLevelUpInput } from './levelUp.validation';

// ---------------------------------------------------------------------------
// `replaces`: quali mosse vengono soppiantate dalla scelta?
// ---------------------------------------------------------------------------

/**
 * Restituisce le `MoveRef` correnti del PG che verrebbero rimosse se
 * prendesse la `move` scelta (in base alla sua `replaces`). Tipicamente
 * 0 o 1 entry; lasciato array per uniformità.
 *
 * Usato dalla UI per mostrare l'avviso «Questa sostituisce *X*» e dal
 * service `applyLevelUp` per il side-effect transazionale.
 */
export function getReplacedMoves(move: Move, character: Character): readonly MoveRef[] {
  if (move.replaces.length === 0) return [];
  return character.moves.filter((ref) => move.replaces.includes(ref.id));
}

// ---------------------------------------------------------------------------
// `applyLevelUp`: transazione atomica
// ---------------------------------------------------------------------------

/**
 * Input dell'evento di level-up. Tutti i campi compilati dall'utente nel
 * flow `LevelUpFlow` prima della conferma finale.
 *
 * **Validazione**: i vincoli (mossa disponibile, stat non al cap, archetipo
 * 2° valido, ecc.) sono enforced da `applyLevelUp` con throw espliciti.
 */
export interface LevelUpInput {
  /**
   * Caratteristica aumentata di +1. Undefined per i livelli post-10° quando
   * `Character.pastTen ∈ {apprendista, instancabile}` (D10 / FI-11: manuale
   * r. 1834 «le tue Caratteristiche non aumenteranno più»). Per L2-L10 è
   * sempre presente.
   */
  statIncreased?: StatId | undefined;
  /**
   * **D10 — FI-11**: scelta cruciale post-10°. Richiesto SOLO quando il PG
   * sta passando da L10 a L11 (`targetLevel === 11`). Ignorato per
   * `targetLevel ≤ 10` o per `targetLevel ≥ 12` (Apprendista/Instancabile
   * già scelti, persistono su `Character.pastTen`).
   *
   * Valori:
   *  - `'addio'`: ritiro definitivo del PG. Il livello SALE a 11 (record
   *    creato), ma `isLevelUpAvailable` ritorna `false` per i successivi.
   *  - `'apprendista'`: continua a crescere come Instancabile. La gestione
   *    del 2° PG (apprentice) è scope Fase F.
   *  - `'instancabile'`: continua a crescere. Caratteristiche non aumentano.
   */
  pastTen?: 'addio' | 'apprendista' | 'instancabile' | undefined;
  /**
   * `MoveRef` della mossa scelta, incluso eventuale setup
   * (`selectedChoices` / `selectedChoiceGroups` / `narrativeFieldValues`).
   */
  moveAdded: MoveRef;
  /**
   * **D2 (FI-33)**: spell aggiunti al pool del PG durante questo
   * level-up. Sorgenti: regola Mago «+1 spell» (via `mago.corpus-arcano`
   * con `trigger: 'on-each-level-up'`), mosse advanced one-shot (Magnus
   * Corpus Arcano, Tomo del Sapere, Rivelazioni Proibite).
   *
   * Default `[]`. Per i PG senza grant attive (non casteggianti, e
   * casteggianti tradizione-native) resta vuoto.
   */
  spellsAdded?: readonly SpellRef[] | undefined;
  /**
   * **FI-46 PR-B+ (2026-05-23)**: mappa raw `grantKey → spellIds[]` delle
   * scelte user-driven (pick) per ogni grant attiva di questo level-up.
   * Propagata 1:1 da `LevelUpDraft.spellSelections` (UI) → record audit
   * (`LevelUpRecord.spellGrantSelections`) per permettere all'edit
   * retroattivo di pre-popolare il draft dello step spell.
   *
   * **Non sostituisce `spellsAdded`**: i due campi sono complementari.
   * `spellsAdded` è la lista piatta usata dal service apply; `spellSelections`
   * è la mappa strutturata usata dall'audit + edit-mode.
   *
   * Opzionale: PG non casteggianti / level-up senza grant pick → undefined.
   */
  spellSelections?: Readonly<Record<string, readonly string[]>> | undefined;
  /**
   * ID del 2° archetipo se la mossa scelta è *Infanzia Complessa*.
   * Deve essere un archetipo della stessa classe del PG, diverso da
   * `archetypeId` corrente.
   */
  secondaryArchetypeId?: string | undefined;
  /**
   * **FI-80 (2026-05-24)** — Setup compilato della mossa-archetipo del 2°
   * archetipo (es. `mago.archetipo-umano` con 18 spell choices). Richiesto
   * SE la mossa-archetipo ha `hasMoveSetup === true`. Mancanza in questo
   * caso → throw da `validateSecondaryArchetype`. Per archetipi la cui
   * mossa-archetipo è description-only (la maggioranza nel catalogo DWR
   * V1) il campo resta `undefined`.
   *
   * Combacia con {@link MoveRef} escluso `id` (`selectedChoices`,
   * `selectedChoiceGroups`, `narrativeFieldValues`).
   */
  secondaryArchetypeSetup?: Omit<MoveRef, 'id'> | undefined;
  /**
   * **D5 (FI-31)**: sotto-scelta compilata se la mossa appena scelta ha
   * `grantsMoveChoice`. Il `selectedOptionId` viene applicato al PG:
   *  - `from-group`: aggiunto come nuovo `MoveRef` in `Character.moves`,
   *    con eventuale setup da `selectedMoveSetup` (se la mossa target
   *    ha `hasMoveSetup`);
   *  - `from-choice-move`: aggiunto come choice al `MoveRef` della mossa
   *    sorgente in `Character.moves` (non crea un nuovo `MoveRef`).
   *
   * **Validazione** (in `applyLevelUp`): la mossa scelta DEVE avere
   * `grantsMoveChoice`; `selectedOptionId` DEVE essere nel pool residuo
   * calcolato da `getGrantedPool`. Mancanza → throw.
   */
  grantedChoice?:
    | {
        selectedOptionId: string;
        /**
         * Setup compilato della mossa target, se la sotto-scelta è
         * `from-group` e la mossa target ha `hasMoveSetup`. Lo schema
         * combacia con `MoveRef` (esclusi `id` e `version`).
         */
        selectedMoveSetup?: Omit<MoveRef, 'id'>;
      }
    | undefined;
  /**
   * **D7 (FI-17)**: pick multiclasse compilato se la `moveAdded` ha
   * `MoveSchema.multiclass` configurato. La mossa-target viene acquisita
   * come `AcquiredMove` con `source: 'multiclass'` via `applyMoveAcquisition`
   * (cross-class via `findMoveByIdGlobal`, già SSOT post-D6).
   *
   * **Validazione** (in `applyLevelUp`): la mossa-trigger DEVE avere
   * `multiclass`; `sourceClassId` DEVE essere nel pool computato da
   * `getMulticlassClassPool`; `selectedMoveId` DEVE essere nel pool
   * computato da `getMulticlassMovePool`. Mancanza → throw.
   *
   * **Bundle all-inclusive (D7-bundle)**: se la mossa-target acquisita ha
   * `bundles` popolato, il bundle viene espanso da `applyMoveAcquisition`.
   * Il setup interattivo delle mosse del bundle viene letto da
   * {@link LevelUpInput.bundleSetups} (top-level, condiviso con main).
   */
  multiclassPick?:
    | {
        sourceClassId: ClassId;
        selectedMoveId: string;
        /**
         * Setup compilato della mossa-target multiclasse, se la mossa ha
         * `hasMoveSetup`. Combacia con `MoveRef` escluso `id`.
         */
        selectedMoveSetup?: Omit<MoveRef, 'id'>;
        /**
         * **FI-84 enhanced (2026-05-24)** — Cascata di mosse concesse via
         * `grantsMoveChoice` sulla mossa-target multiclass (e ricorsivamente
         * sulle granted di livello superiore). Sostituisce i campi
         * separati `grantedOfTargetSetup` + `grantedOfTargetSelectedMoveId`
         * usati da FI-81 #2 / FI-84 base — la cascata SSOT ricorsiva
         * (`applyMoveAcquisition.grantedCascadeSteps`) li copre uniformemente.
         *
         * Forma: lista ordinata di step. `[0]` = mossa granted dalla
         * target. `[1]` = mossa granted dalla `[0]`. Etc.
         *
         * Caso pilota cascade profondo: PG Guerriero → *Compagno d'Armi*
         * → Faro (multiclass) → `grantedChain[0]` = Ordine Sovrannaturale
         * (scelta da from-list di Faro) → `grantedChain[1]` = Vincolo
         * della Lama (mappata da from-choice-mapping di Ordine Sovr).
         */
        grantedChain?: readonly {
          selectedMoveId: string;
          selectedSetup?: Omit<MoveRef, 'id'>;
        }[];
      }
    | undefined;
  /**
   * **D7-bundle (FI-17 follow-up)**: setup compilato per le mosse del
   * bundle che hanno `hasMoveSetup === true`. Top-level e condiviso tra
   * `moveAdded` (main) e `multiclassPick.selectedMoveId` (multiclass) —
   * un singolo level-up ha al massimo 1 mossa-anchor di bundle, quindi
   * gli ID sono disambiguati naturalmente.
   *
   * Chiavi: `moveId` della mossa del bundle. Valori: campi di
   * {@link MoveRef} escluso `id` (`selectedChoices`,
   * `selectedChoiceGroups`, `narrativeFieldValues`).
   *
   * Esempio Guerriero L2 → *Combattente Multiclasse* → *Lanciare
   * Incantesimi Divini* del Chierico → bundle *Divinità* + *Mana*:
   * ```ts
   * bundleSetups: {
   *   'chierico.divinita': {
   *     selectedChoiceGroups: { 'tradizione-sacra': ['valore'], ... },
   *     narrativeFieldValues: { 'nome-divinita': 'Pelor' },
   *   }
   * }
   * ```
   * `chierico.mana` non ha setup → chiave assente.
   *
   * Se una mossa del bundle ha `hasMoveSetup === true` e la chiave
   * corrispondente è assente, `applyMoveAcquisition` lancia errore.
   */
  bundleSetups?: Readonly<Record<string, Omit<MoveRef, 'id'>>>;
}

/**
 * Applica un level-up transazionale al PG. Tutte le mutazioni atomiche.
 *
 * **Scope Fase D** (build planner, non progressione in gioco): NON
 * consuma `xp` (la sottrazione `level + 7` PE è rinviata a Fase F).
 *
 * Sequenza di mutazioni:
 *  1. (rinviato a Fase F) `xp -= getXpRequired(character)`
 *  2. `level += 1`
 *  3. `stats[statIncreased] += 1` se applicabile; ricalcola HP se cambia COS
 *  4. `moves += moveAdded`
 *  5. `moves -= movesReplaced` (dalla `replaces` della mossa scelta)
 *  6. `moves += movesBundled` (pacchetto all-inclusive, D7-bundle) —
 *     espanso da `applyMoveAcquisition` se la mossa anchor (main o
 *     multiclass-target) ha `move.bundles` popolato
 *  7. `spells += spellsAdded` (D2/FI-33): regola Mago «+1 spell»
 *     ricorrente + mosse-grant `on-take` (Magnus / Tomo / Rivelazioni)
 *  8. `secondaryArchetypeId = ...` se *Infanzia Complessa* + ricalcolo HP
 *     se il nuovo archetipo ha `baseHpOverride` diverso dal corrente
 *  9. `followers` re-derivato da `getFollowersFromDraft` (FI-8a) per
 *     gestire `Famiglio` e altri template che derivano da mosse-source
 * 10. `levelUpHistory += new LevelUpRecord(...)`
 * 11. `updatedAt = now`
 *
 * Re-valida il Character risultante via `CharacterSchema.parse()`.
 *
 * @throws Error se l'input è invalido (mossa non disponibile, stat al cap,
 *               IC con archetipo già pari al primario, ecc.)
 */
export function applyLevelUp(
  character: Character,
  input: LevelUpInput,
  classDef: ClassDefinition,
): Character {
  // **D10 — FI-11**: path Addio. Il PG si ritira: livello sale +1 per
  // traccia (record/audit) ma niente mossa/stat/follower derivati. Setup
  // dei successivi level-up bloccato da `isLevelUpAvailable` (pastTen=addio).
  if (input.pastTen === 'addio') {
    return applyRetirement(character);
  }
  validateLevelUpInput(character, input, classDef);

  const nextLevel = character.level + 1;
  const nextStats = applyStatIncrement(character, input.statIncreased);

  const moveDef = findMoveByIdAnyScope(classDef.id, input.moveAdded.id);
  if (!moveDef) {
    throw new Error(`Mossa con id "${input.moveAdded.id}" non trovata nel catalogo.`);
  }

  // **FI-35 (D-FI35)**: delega l'applicazione "moves + spell auto della
  // mossa" al service SSOT `applyMoveAcquisition`. La mossa principale
  // è acquisita con `source: 'main'`; la sotto-scelta granted (se
  // applicabile e di kind `from-group`) come `source: 'granted'`. Per
  // `from-choice-move` (estensione di MoveRef esistente, non nuova
  // acquisizione) resta una mutazione locale del `MoveRef` sorgente.
  const mainAcquired: AcquiredMove = {
    moveRef: input.moveAdded,
    source: 'main',
    spellsSelected: [],
  };
  // **D7-bundle**: `bundleSetups` è top-level e propagato a tutti i passi
  // di acquisizione. Le mosse del bundle vengono espanse automaticamente
  // da `applyMoveAcquisition` quando la mossa anchor ha `move.bundles`.
  // Costruzione condizionale per `exactOptionalPropertyTypes`.
  const acqOptions: ApplyMoveAcquisitionOptions =
    input.bundleSetups !== undefined ? { bundleSetups: input.bundleSetups } : {};
  const afterMain = applyMoveAcquisition(character, mainAcquired, classDef, acqOptions);
  const afterGranted = applyGrantedAcquisition(
    afterMain,
    moveDef,
    input.grantedChoice,
    classDef,
    acqOptions,
  );
  // **D7 (FI-17)**: applica `multiclassPick` se la mossa-trigger ha
  // `multiclass` configurato. La mossa-target è cross-class — `applyMoveAcquisition`
  // fa lookup globale via `findMoveByIdGlobal`. Il bundle della mossa-target
  // (es. *Lanciare Incantesimi Arcani* → +*Corpus Arcano* +*Mana*) viene
  // espanso automaticamente da `applyMoveAcquisition` (D7-bundle).
  const afterMulticlass = applyMulticlassAcquisition(
    afterGranted,
    moveDef,
    input.multiclassPick,
    classDef,
    acqOptions,
  );
  // **FI-80 (2026-05-24)**: se IC ha settato un 2° archetipo, acquisisci la
  // sua `grantedMoveId` (mossa-archetipo) come `source: 'archetype'` con
  // setup propagato dall'input. La mossa-archetipo può a sua volta avere
  // setup interattivo (es. `mago.archetipo-umano` con 18 spell choices) →
  // `secondaryArchetypeSetup` propagato. Bundle expansion attiva di default
  // (nessuna mossa-archetipo DWR V1 ha `bundles`, ma è coerente).
  const afterSecondaryArchetype = applySecondaryArchetypeAcquisition(
    afterMulticlass,
    input.secondaryArchetypeId,
    input.secondaryArchetypeSetup,
    classDef,
    acqOptions,
  );

  // Ricalcolo HP: cambia se aumenta COS, oppure se il nuovo
  // `secondaryArchetypeId` introduce un `baseHpOverride` (FI-1 esteso).
  const nextHp = recomputeHp(
    character,
    nextStats.costituzione,
    classDef,
    input.secondaryArchetypeId ?? character.secondaryArchetypeId,
  );

  const record: LevelUpRecord = buildLevelUpRecord(
    input,
    moveDef,
    character.moves,
    afterSecondaryArchetype.moves,
    nextLevel,
  );

  const updatedAt = new Date().toISOString();

  // D2 / FI-33: nuovi spell aggiunti via grant aggregati dal caller
  // (regola Mago `on-each-level-up` + grant pick `on-take` della mossa
  // appena presa). Le grant `on-take` AUTO sono già state applicate da
  // `applyMoveAcquisition` (sia main, granted, sia multiclass). Dedup
  // contro le spell già accumulate.
  const existingSpellIds = new Set(afterSecondaryArchetype.spells.map((s) => s.id));
  const newSpells = (input.spellsAdded ?? []).filter((s) => !existingSpellIds.has(s.id));
  const nextSpells = [...afterSecondaryArchetype.spells, ...newSpells];

  // **D10 — FI-11**: applica `pastTen` se siamo al crocevia L10 → L11.
  // Per livelli successivi (L11+), `pastTen` su `character` resta invariato.
  const nextPastTen = input.pastTen ?? character.pastTen;
  const candidate: Character = {
    ...afterSecondaryArchetype,
    level: nextLevel,
    stats: nextStats,
    hp: nextHp,
    spells: nextSpells,
    secondaryArchetypeId: input.secondaryArchetypeId ?? character.secondaryArchetypeId,
    ...(nextPastTen !== undefined ? { pastTen: nextPastTen } : {}),
    levelUpHistory: [...character.levelUpHistory, record],
    updatedAt,
  };

  // Re-derivazione follower dalle mosse-source con `followerTemplateId`
  // (FI-8a / FI-8b). Se la mossa appena presa è il Famiglio del Mago,
  // il follower viene popolato qui.
  candidate.followers = getFollowersFromDraft(classDef.id, candidate.moves, candidate.level);

  return CharacterSchema.parse(candidate);
}

/**
 * **FI-80 (2026-05-24)** — Acquisisce la mossa-archetipo del 2° archetipo
 * concesso via *Infanzia Complessa*.
 *
 * Risolve `secondaryArchetypeId` → `classDef.archetypes[*].grantedMoveId`,
 * costruisce il `MoveRef` con eventuale `secondaryArchetypeSetup`
 * propagato, e delega a {@link applyMoveAcquisition} con
 * `source: 'archetype'` (le grants spell + bundle expansion vengono
 * gestite uniformemente dal service SSOT).
 *
 * **No-op** se `secondaryArchetypeId` è `undefined` o se l'archetipo non
 * è nel `classDef` (caso patologico, già coperto da
 * `validateSecondaryArchetype`).
 *
 * Coerente con il pattern delle altre acquisizioni nested
 * (`applyGrantedAcquisition`, `applyMulticlassAcquisition`).
 */
function applySecondaryArchetypeAcquisition(
  character: Character,
  secondaryArchetypeId: string | undefined,
  secondaryArchetypeSetup: Omit<MoveRef, 'id'> | undefined,
  classDef: ClassDefinition,
  options: ApplyMoveAcquisitionOptions = {},
): Character {
  if (secondaryArchetypeId === undefined) return character;
  const arch = classDef.archetypes.find((a) => a.id === secondaryArchetypeId);
  if (arch === undefined) return character;
  const archetypeMoveRef: MoveRef = secondaryArchetypeSetup
    ? { id: arch.grantedMoveId, ...secondaryArchetypeSetup }
    : { id: arch.grantedMoveId, selectedChoices: [] };
  const acquired: AcquiredMove = {
    moveRef: archetypeMoveRef,
    source: 'archetype',
    spellsSelected: [],
  };
  return applyMoveAcquisition(character, acquired, classDef, options);
}

/**
 * **FI-35 (D-FI35)** + **FI-30 (D6)**: applica la sotto-scelta granted
 * al Character.
 *
 *  - `from-group` (FI-31): la sotto-scelta è una nuova mossa acquisita
 *    della stessa classe del PG (`source: 'granted'`) → delega a
 *    `applyMoveAcquisition` per gestire spell auto / replaces / etc.
 *    della mossa concessa.
 *  - `from-choice-move` (FI-31): la sotto-scelta è una nuova choice nel
 *    MoveRef della mossa sorgente esistente → mutazione locale, niente
 *    nuova acquisizione.
 *  - `from-choice-mapping` (FI-30): la mossa concessa è cross-class,
 *    derivata dal mapping. Stesso flow di `from-group` (acquisizione
 *    via `applyMoveAcquisition`) ma la mossa target è di un'altra classe
 *    — `applyMoveAcquisition` fa lookup globale.
 *  - Nessuna grant → ritorna il Character invariato.
 */
function applyGrantedAcquisition(
  character: Character,
  moveDef: Move,
  grantedChoice: LevelUpInput['grantedChoice'],
  classDef: ClassDefinition,
  options: ApplyMoveAcquisitionOptions = {},
): Character {
  if (moveDef.grantsMoveChoice === undefined || grantedChoice === undefined) {
    return character;
  }
  const grant = moveDef.grantsMoveChoice;
  if (
    grant.kind === 'from-group' ||
    grant.kind === 'from-choice-mapping' ||
    grant.kind === 'from-list'
  ) {
    // **FI-82**: `from-list` segue lo stesso flow di acquisition (nuova
    // mossa aggiunta come `source: 'granted'`); l'unica differenza è la
    // sorgente del pool (inline su moveDef.grantsMoveChoice.moveOptions
    // invece di lookup classDef.startingChoiceGroups o mapping cross-class).
    const grantedMoveRef: MoveRef = grantedChoice.selectedMoveSetup
      ? { id: grantedChoice.selectedOptionId, ...grantedChoice.selectedMoveSetup }
      : { id: grantedChoice.selectedOptionId, selectedChoices: [] };
    const grantedAcquired: AcquiredMove = {
      moveRef: grantedMoveRef,
      source: 'granted',
      spellsSelected: [],
    };
    return applyMoveAcquisition(character, grantedAcquired, classDef, options);
  }
  // from-choice-move: estensione del MoveRef sorgente (no nuova acquisizione)
  return { ...character, moves: extendSourceMoveRef(character.moves, grant, grantedChoice) };
}

/**
 * **FI-17 (D7)**: applica la pick multiclasse al Character, acquisendo
 * la mossa-target cross-class via `applyMoveAcquisition` con
 * `source: 'multiclass'`. Lookup globale via `findMoveByIdGlobal`
 * (SSOT post-D6).
 *
 * **Bundle**: se la mossa-target ha `bundles` popolato, il flow di bundle
 * vive in PR successivo D7-bundle. Qui non viene applicato.
 *
 * **No multiclass o no pick**: ritorna il Character invariato.
 */
function applyMulticlassAcquisition(
  character: Character,
  moveDef: Move,
  multiclassPick: LevelUpInput['multiclassPick'],
  classDef: ClassDefinition,
  options: ApplyMoveAcquisitionOptions = {},
): Character {
  if (moveDef.multiclass === undefined || multiclassPick === undefined) {
    return character;
  }
  const targetMoveRef: MoveRef = multiclassPick.selectedMoveSetup
    ? { id: multiclassPick.selectedMoveId, ...multiclassPick.selectedMoveSetup }
    : { id: multiclassPick.selectedMoveId, selectedChoices: [] };
  const acquired: AcquiredMove = {
    moveRef: targetMoveRef,
    source: 'multiclass',
    spellsSelected: [],
  };
  // **FI-84 enhanced (2026-05-24)** — SSOT ricorsiva apply layer.
  // Costruisce `grantedCascadeSteps` per `applyMoveAcquisition`,
  // unificando i pattern FI-81 #2 (from-choice-mapping auto-derivato)
  // e FI-84 base (from-list esplicito) + cascade granted-of-granted
  // di N livelli.
  const cascadeSteps = buildMulticlassGrantedCascade(multiclassPick, targetMoveRef);
  const acqOptions: ApplyMoveAcquisitionOptions = { ...options };
  if (cascadeSteps.length > 0) acqOptions.grantedCascadeSteps = cascadeSteps;
  return applyMoveAcquisition(character, acquired, classDef, acqOptions);
}

/**
 * **FI-84 enhanced (2026-05-24)** — Costruisce la cascata di granted
 * steps da `multiclassPick`, unificando i 2 pattern legacy:
 *  - `from-choice-mapping`: il primo step viene auto-derivato dal
 *    mapping della `selectedChoices[0]` del target setup. Steps
 *    successivi vengono dal `grantedChain`.
 *  - `from-list`: tutti gli step vengono direttamente da `grantedChain`.
 *
 * **Compat semantica**: per `from-choice-mapping`, se la mossa derivata
 * dal mapping coincide col primo elemento del `grantedChain`, evita
 * duplicato. Caso edge: caller passa `grantedChain` con un'altra mossa
 * (incoerenza che la validation dovrebbe avere già bloccato).
 */
function buildMulticlassGrantedCascade(
  multiclassPick: NonNullable<LevelUpInput['multiclassPick']>,
  targetMoveRef: MoveRef,
): readonly { selectedMoveId: string; selectedSetup?: Omit<MoveRef, 'id'> }[] {
  const targetMove = findMoveByIdGlobal(multiclassPick.selectedMoveId);
  const grant = targetMove?.grantsMoveChoice;
  if (grant === undefined) return [];
  const explicitChain = multiclassPick.grantedChain ?? [];
  // Per `from-choice-mapping`: deriva auto-step dal mapping se non già
  // nella chain esplicita. Le mosse del mapping sono concesse
  // automaticamente — l'utente NON deve sceglierle (deriva dalla choice
  // del target setup).
  if (grant.kind === 'from-choice-mapping') {
    const selectedChoice = targetMoveRef.selectedChoices[0];
    if (selectedChoice === undefined) return explicitChain;
    const mappedId = grant.mapping[selectedChoice];
    if (mappedId === undefined) return explicitChain;
    // Se l'utente ha già esplicitato il primo step come la mossa mappata,
    // riusa la chain (rispetta il setup). Altrimenti prepend lo step.
    if (explicitChain[0]?.selectedMoveId === mappedId) return explicitChain;
    return [{ selectedMoveId: mappedId }, ...explicitChain];
  }
  // Per `from-list` (e altri kind futuri): tutti gli step dal chain.
  return explicitChain;
}

/**
 * **FI-31 (D5)** + **FI-35 (D-FI35)**: estende `selectedChoices` (e
 * `selectedChoiceGroups.main` per coerenza con proiezione UI) del MoveRef
 * sorgente di una grant `from-choice-move`.
 */
function extendSourceMoveRef(
  moves: readonly MoveRef[],
  grant: { kind: 'from-choice-move'; moveId: string },
  grantedChoice: NonNullable<LevelUpInput['grantedChoice']>,
): MoveRef[] {
  return moves.map((m) => {
    if (m.id !== grant.moveId) return m;
    const extended: MoveRef = {
      ...m,
      selectedChoices: [...m.selectedChoices, grantedChoice.selectedOptionId],
    };
    if (m.selectedChoiceGroups?.main !== undefined) {
      extended.selectedChoiceGroups = {
        ...m.selectedChoiceGroups,
        main: [...m.selectedChoiceGroups.main, grantedChoice.selectedOptionId],
      };
    }
    return extended;
  });
}

function applyStatIncrement(character: Character, statId: StatId | undefined): Character['stats'] {
  if (statId === undefined) return character.stats;
  return { ...character.stats, [statId]: character.stats[statId] + 1 };
}

function recomputeHp(
  character: Character,
  nextCostituzione: number,
  classDef: ClassDefinition,
  effectiveSecondaryArchetypeId: string | undefined,
): Character['hp'] {
  // FI-45 C5: delega a computeMaxHp (SSOT) — formula identica a L1.
  const nextMax = computeMaxHp(
    classDef,
    nextCostituzione,
    character.archetypeId,
    effectiveSecondaryArchetypeId,
  );
  const delta = nextMax - character.hp.max;
  // Per «PF attuali» applichiamo lo stesso delta: se PG era 18/24 → 19/25
  // (manuale r. 1759 + decisione D-Q14 SSOT Fase D).
  const nextCurrent = Math.max(0, character.hp.current + delta);
  return { current: nextCurrent, max: nextMax };
}

// `buildLevelUpRecord` + `buildGrantedChoiceAudit` estratti in
// `./levelUp.audit.ts` (FI-17/D7) per rispettare `max-lines: 400` su
// questo file.

// `validateLevelUpInput` + sub-validators (validatePastTen / validateStatIncrement /
// validateMoveAvailability / validateSecondaryArchetype / validateGrantedChoice /
// validateMulticlassPickInput) estratti in `./levelUp.validation.ts` per
// rispettare `max-lines: 400` (D10).

/**
 * **D10 — FI-11**: applica L'Addio. Il PG si ritira; nessuna mossa
 * aggiunta, nessuna stat aumentata. Livello sale +1 solo per traccia
 * storica. Il `pastTen` è settato a `'addio'` e i successivi `applyLevelUp`
 * sono bloccati da `isLevelUpAvailable`.
 *
 * Non genera un `LevelUpRecord` "standard" perché manca `moveAdded`. Pattern
 * di traccia alternativo (es. `Character.retirementEvent`) può essere
 * introdotto in Fase F (giocabile).
 */
function applyRetirement(character: Character): Character {
  if (character.level < LEVEL_FORMULA_CAP) {
    throw new Error(
      `L'Addio si applica solo al passaggio L10 → L11. Il PG è a L${character.level}.`,
    );
  }
  if (character.pastTen !== undefined) {
    throw new Error(
      `Il PG ha già scelto pastTen='${character.pastTen}'. La scelta è irreversibile.`,
    );
  }
  return CharacterSchema.parse({
    ...character,
    level: character.level + 1,
    pastTen: 'addio',
    updatedAt: new Date().toISOString(),
  });
}

// Esportato come helper di test (usato indirettamente da `applyLevelUp`).
export { findMoveById };
